import React from 'react';
import type { ErrorResponse } from '@/api/handlers/responses';
import type {
    AttributeType,
    CategoryAttributeItem,
    CategoryAttributeRole,
} from '@/api/models';
import {
    useAttributeTypesQuery,
    useCategoryAttributesQuery,
    usePutCategoryAttributesMutation,
} from '@/api/queries';
import {
    buildDraftFromItems,
    getAvailableTypes,
    isDraftDirty,
    toWriteItems,
    type CategoryAttributeDraft,
} from './categoryAttributesMeta';

type UseCategoryAttributesSheetParams = {
    /** The category being managed, or `null` while the drawer is closed. */
    categoryId: string | null;
    /** Called after a successful save so the parent can close the drawer. */
    onSaved: () => void;
};

/**
 * Logic for the per-category attribute drawer (Surface A). Loads the category's
 * attribute set into an editable **local draft**, lets the user add/remove
 * dictionary attributes, toggle each one's role/required flag, and reorder
 * them, then resolves the whole draft to one full-replace PUT on save.
 *
 * The draft is intentionally decoupled from the query: edits are local until
 * the user saves (Cancel discards them). On a successful save the parent closes
 * the drawer (`onSaved`), which drops `categoryId` to `null` and resets state.
 */
export const useCategoryAttributesSheet = ({
    categoryId,
    onSaved,
}: UseCategoryAttributesSheetParams) => {
    const attributesQuery = useCategoryAttributesQuery(categoryId);
    // The dictionary only matters while the drawer is open (it feeds the picker).
    const typesQuery = useAttributeTypesQuery(categoryId !== null);
    const putMutation = usePutCategoryAttributesMutation();

    const [draft, setDraft] = React.useState<CategoryAttributeDraft[] | null>(
        null,
    );
    const [saveError, setSaveError] = React.useState<string | null>(null);

    const serverItems = React.useMemo<CategoryAttributeItem[] | null>(() => {
        if (!attributesQuery.data || !attributesQuery.data.success) {
            return null;
        }
        return attributesQuery.data.response.items;
    }, [attributesQuery.data]);

    const loadError: ErrorResponse | null =
        attributesQuery.data && !attributesQuery.data.success
            ? attributesQuery.data.error
            : null;

    // Reset whenever the managed category changes — including to `null` on
    // close. Keeps one category's edits from bleeding into the next open.
    React.useEffect(() => {
        setDraft(null);
        setSaveError(null);
    }, [categoryId]);

    // Seed the draft once this category's persisted set lands. `draft === null`
    // gates it to the first load, so a post-save refetch never clobbers edits
    // (on save the drawer closes, which resets the draft for next time).
    React.useEffect(() => {
        if (draft === null && serverItems !== null) {
            setDraft(buildDraftFromItems(serverItems));
        }
    }, [draft, serverItems]);

    const availableTypes = React.useMemo<AttributeType[]>(() => {
        if (draft === null || !typesQuery.data || !typesQuery.data.success) {
            return [];
        }
        return getAvailableTypes(typesQuery.data.response.items, draft);
    }, [draft, typesQuery.data]);

    const isDirty = React.useMemo(() => {
        if (draft === null || serverItems === null) {
            return false;
        }
        return isDraftDirty(draft, serverItems);
    }, [draft, serverItems]);

    const onAddType = React.useCallback(
        (attributeTypeId: string) => {
            setDraft((current) => {
                if (current === null) {
                    return current;
                }
                if (
                    current.some(
                        (item) => item.attributeTypeId === attributeTypeId,
                    )
                ) {
                    return current;
                }
                const type = availableTypes.find(
                    (candidate) => candidate.id === attributeTypeId,
                );
                if (type === undefined) {
                    return current;
                }
                return [
                    ...current,
                    {
                        attributeTypeId,
                        role: 'Descriptive' as CategoryAttributeRole,
                        isRequired: false,
                        attributeType: type,
                    },
                ];
            });
        },
        [availableTypes],
    );

    const onRemoveType = React.useCallback((attributeTypeId: string) => {
        setDraft((current) =>
            current === null
                ? current
                : current.filter(
                      (item) => item.attributeTypeId !== attributeTypeId,
                  ),
        );
    }, []);

    const onRoleChange = React.useCallback(
        (attributeTypeId: string, role: CategoryAttributeRole) => {
            setDraft((current) =>
                current === null
                    ? current
                    : current.map((item) =>
                          item.attributeTypeId === attributeTypeId
                              ? { ...item, role }
                              : item,
                      ),
            );
        },
        [],
    );

    const onRequiredChange = React.useCallback(
        (attributeTypeId: string, isRequired: boolean) => {
            setDraft((current) =>
                current === null
                    ? current
                    : current.map((item) =>
                          item.attributeTypeId === attributeTypeId
                              ? { ...item, isRequired }
                              : item,
                      ),
            );
        },
        [],
    );

    const onMove = React.useCallback(
        (attributeTypeId: string, direction: 'up' | 'down') => {
            setDraft((current) => {
                if (current === null) {
                    return current;
                }
                const from = current.findIndex(
                    (item) => item.attributeTypeId === attributeTypeId,
                );
                const to = direction === 'up' ? from - 1 : from + 1;
                if (from < 0 || to < 0 || to >= current.length) {
                    return current;
                }
                const next = [...current];
                const moved = next[from]!;
                next[from] = next[to]!;
                next[to] = moved;
                return next;
            });
        },
        [],
    );

    const onSave = React.useCallback(async () => {
        if (categoryId === null || draft === null) {
            return;
        }
        setSaveError(null);
        const result = await putMutation.mutateAsync({
            categoryId,
            body: { items: toWriteItems(draft) },
        });
        if (!result.success) {
            setSaveError(result.error.message ?? 'Failed to save attributes.');
            return;
        }
        onSaved();
    }, [categoryId, draft, onSaved, putMutation]);

    const onRetry = React.useCallback(() => {
        attributesQuery.refetch();
    }, [attributesQuery]);

    return {
        draft: draft ?? [],
        availableTypes,
        isDirty,
        isLoading: categoryId !== null && draft === null && loadError === null,
        loadError,
        saveError,
        isSaving: putMutation.isPending,
        onAddType,
        onRemoveType,
        onRoleChange,
        onRequiredChange,
        onMove,
        onSave,
        onRetry,
    };
};
